/**
 * Data Integrity Utility for Outcomes Questions
 * 
 * Ensures that outcome IDs are unique and correctly mapped.
 * If duplicate IDs are found, uses the "q" field from the first question
 * in each outcome's questions array as the true ID.
 */

/**
 * Checks for duplicate IDs in the outcomes questions array
 * @param {Array} questions - Array of outcome question objects
 * @returns {Object} - { hasDuplicates: boolean, duplicateIds: Array }
 */
export const checkForDuplicateIds = (questions) => {
  if (!questions || !Array.isArray(questions)) {
    return { hasDuplicates: false, duplicateIds: [] };
  }

  const idCounts = {};
  const duplicateIds = [];

  questions.forEach((outcome) => {
    const id = outcome.id;
    idCounts[id] = (idCounts[id] || 0) + 1;
  });

  Object.keys(idCounts).forEach((id) => {
    if (idCounts[id] > 1) {
      duplicateIds.push(Number(id));
    }
  });

  return {
    hasDuplicates: duplicateIds.length > 0,
    duplicateIds,
  };
};

/**
 * Checks if IDs increment correctly (no gaps, proper sequence)
 * @param {Array} questions - Array of outcome question objects
 * @returns {Object} - { isSequential: boolean, gaps: Array, expectedSequence: Array }
 */
export const checkIdSequence = (questions) => {
  if (!questions || !Array.isArray(questions) || questions.length === 0) {
    return { isSequential: true, gaps: [], expectedSequence: [] };
  }

  const ids = questions.map((outcome) => outcome.id).sort((a, b) => a - b);
  const gaps = [];
  const expectedSequence = [];

  for (let i = 0; i < ids.length; i++) {
    const expectedId = i + 1;
    expectedSequence.push(expectedId);

    if (ids[i] !== expectedId) {
      gaps.push({ expected: expectedId, actual: ids[i] });
    }
  }

  return {
    isSequential: gaps.length === 0,
    gaps,
    expectedSequence,
  };
};

/**
 * Fixes duplicate IDs by using the "q" field from the first question
 * in each outcome's questions array
 * @param {Array} questions - Array of outcome question objects
 * @returns {Array} - Sanitized array with corrected IDs
 */
export const fixDuplicateIds = (questions) => {
  if (!questions || !Array.isArray(questions)) {
    return questions;
  }

  return questions.map((outcome) => {
    // Get the "q" value from the first question in this outcome's questions array
    const trueId = outcome.questions?.[0]?.q;

    if (trueId !== undefined && trueId !== null) {
      return {
        ...outcome,
        id: Number(trueId),
      };
    }

    // If no "q" field found, return as-is
    return outcome;
  });
};

/**
 * Main function to validate and sanitize outcomes questions data
 * @param {Object} outcomesQuestions - The outcomesQuestions object with questions array
 * @returns {Object} - { sanitized: Object, integrity: Object }
 */
export const validateAndSanitizeOutcomes = (outcomesQuestions) => {
  if (!outcomesQuestions?.questions) {
    return {
      sanitized: outcomesQuestions,
      integrity: {
        isValid: true,
        hasDuplicates: false,
        isSequential: true,
        wasFixed: false,
      },
    };
  }

  const questions = outcomesQuestions.questions;

  // Check for duplicates
  const duplicateCheck = checkForDuplicateIds(questions);

  // Check sequence
  const sequenceCheck = checkIdSequence(questions);

  // Log integrity issues if found
  if (duplicateCheck.hasDuplicates || !sequenceCheck.isSequential) {
    console.warn('[OutcomesDataIntegrity] Data integrity issues detected:', {
      duplicates: duplicateCheck,
      sequence: sequenceCheck,
      originalData: questions,
    });
  }

  // Fix duplicates if they exist
  let sanitizedQuestions = questions;
  let wasFixed = false;

  if (duplicateCheck.hasDuplicates) {
    sanitizedQuestions = fixDuplicateIds(questions);
    wasFixed = true;

    console.log('[OutcomesDataIntegrity] IDs fixed using "q" field:', {
      original: questions.map((q) => ({ id: q.id, name: q.name })),
      fixed: sanitizedQuestions.map((q) => ({ id: q.id, name: q.name })),
    });

    // Re-check after fix
    const postFixDuplicateCheck = checkForDuplicateIds(sanitizedQuestions);
    if (postFixDuplicateCheck.hasDuplicates) {
      console.error(
        '[OutcomesDataIntegrity] WARNING: Duplicates still exist after fix!',
        postFixDuplicateCheck
      );
    }
  }

  return {
    sanitized: {
      ...outcomesQuestions,
      questions: sanitizedQuestions,
    },
    integrity: {
      isValid: !duplicateCheck.hasDuplicates && sequenceCheck.isSequential,
      hasDuplicates: duplicateCheck.hasDuplicates,
      duplicateIds: duplicateCheck.duplicateIds,
      isSequential: sequenceCheck.isSequential,
      gaps: sequenceCheck.gaps,
      wasFixed,
    },
  };
};

